import { useState } from "react";
import { t } from "@/utils";
import { getHasPermissionError } from "@/modules/stream/permissions";
import environment from "@/modules/environment";
import { getPairingCodeInfo } from "viewer/modules/cloud/pairing";
import { viewerStore } from "viewer/store/viewer";
import { Flex, Button, Heading } from "@/ui";
import { useButtonStateColors } from "@/theme/utils/getButtonStateColor";
import { useControls } from "@/hooks";
import Dialog from "@/components/Dialog";
import NavigationBar from "@/components/NavigationBar";
import Icon from "@/components/Icon";
import HelpIcon from "assets/icons/help_outlined.svg?react";
import { WizardProps } from "../../Wizard";
import PairingCodeFailureDialog from "../EnterCode/PairingCodeFailureDialog";
import PairingQrCodeFailureDialog from "./PairingQrCodeFailureDialog";
import Scanner from "./Scanner";
import CrashReporter from "@/modules/app/CrashReporter";

const { appName } = environment;

export default function ScanCode({ push, previous, requestManager }: WizardProps) {
  const alreadyPairedDialog = useControls();
  const errorDialog = useControls();
  const scanErrorDialog = useControls();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<PairingCodeInfoError | null>(null);
  const [responderDeviceName, setResponderDeviceName] = useState("");
  const { active, hover } = useButtonStateColors("neutral.99");

  function handleScannerError(err: unknown) {
    const message = err instanceof Error ? err.message : String(err);
    const errorType = getHasPermissionError(message) ? "no_permission" : "no_device";
    viewerStore().setLocalDevice("camera", { askedForPermission: true, error: errorType });
    push("enter-code");
  }

  async function handleScan(result: { rawValue: string }[]) {
    if (isProcessing || !result.length) return;
    const url = result[0].rawValue;

    if (!url.includes(appName)) {
      CrashReporter.sendMessage(`qr_pairing : QR code not from ${appName}: ${url}`);
      scanErrorDialog.open();
      return;
    }

    const numericCode = url.replace(/\D/g, "");
    if (numericCode.length !== 9) return;

    setIsProcessing(true);

    try {
      const codeInfo = await getPairingCodeInfo(numericCode, { rethrow: true });
      if (codeInfo.isPaired) {
        CrashReporter.sendMessage(`qr_pairing : QR code ${url} state: already_paired`);
        setResponderDeviceName(codeInfo.deviceName);
        alreadyPairedDialog.open();
        return;
      }
      requestManager.initiatePairing({
        push,
        responder: { jid: codeInfo.xmppLogin, name: codeInfo.deviceName, code: codeInfo.code }
      });
    } catch (err: any) {
      log.err(err);
      setError(err.message as PairingCodeInfoError);
      CrashReporter.sendMessage(`qr_pairing : QR code : ${url} state: ${err.message}`);
      errorDialog.open();
    }
  }

  function goToHelp() {
    scanErrorDialog.close();
    push("cant-find-code");
  }

  function resetState() {
    setIsProcessing(false);
    setResponderDeviceName("");
    setError(null);
  }
  return (
    <Flex
      grow={1}
      direction="column"
      alignItems="center"
      pos="relative"
      mx="-32px"
      mt="-8px"
      mb="-40px"
      overflow="hidden"
    >
      <NavigationBar
        iconsColor="neutral.99"
        onBack={() => previous()}
        props={{ position: "absolute", left: "32px", top: "20px" }}
      />
      <Button
        variant="textFixedSmall"
        onClick={() => push("cant-find-code")}
        color="neutral.99"
        _hover={{ bg: hover }}
        _active={{ bg: active }}
        pos="absolute"
        right="20"
        top="12"
        py="8"
      >
        <Icon icon={HelpIcon} fill="neutral.99" style={{ mr: "0.25rem" }} />
        {t("pairing.code.help.title")}
      </Button>

      <Flex grow={1} direction="column" alignItems="center" justifyContent="center">
        <Scanner handleScan={handleScan} handleScannerError={handleScannerError} />
        <Heading as="h4" color="neutral.99" mt="2rem">
          {t("pairing.scanFromSecondDevice")}
        </Heading>
      </Flex>
      <Button
        variant="outlineFixedMedium"
        onClick={() => push("enter-code")}
        borderColor="neutral.99"
        color="neutral.99"
        mb="40px"
      >
        {t("enterCodeManually")}
      </Button>
      <Dialog
        isOpen={alreadyPairedDialog.isOpen}
        onClose={alreadyPairedDialog.close}
        title={t("pairing.manually.enterCode.alreadyPairedDialog.heading")}
        body={t("pairing.manually.enterCode.alreadyPairedDialog.text", { 0: responderDeviceName })}
        props={{ onCloseComplete: resetState, blockScrollOnMount: false }}
      />
      <PairingQrCodeFailureDialog
        isOpen={scanErrorDialog.isOpen}
        onClose={scanErrorDialog.close}
        goToHelp={goToHelp}
        props={{ onCloseComplete: resetState, blockScrollOnMount: false }}
      />

      <PairingCodeFailureDialog
        isOpen={errorDialog.isOpen}
        close={errorDialog.close}
        errorMessage={error}
        props={{ onCloseComplete: resetState, blockScrollOnMount: false }}
      />
    </Flex>
  );
}
